import { t } from '@lingui/core/macro';
import {
  flexRender,
  type Header,
  type HeaderGroup,
  type SortingState,
} from '@tanstack/react-table';
import { useState } from 'react';

import FilterAppliedIcon from '@/assets/svg/bx-filter-alt.svg?react';
import FilterIcon from '@/assets/svg/bx-filter.svg?react';
import SortDownIcon from '@/assets/svg/bx-sort-down.svg?react';
import SortUpIcon from '@/assets/svg/bx-sort-up.svg?react';
import type { FilterOperatorValue } from '@/components/InstantSearch/types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { TableHead, TableHeader, TableRow } from '@/components/Table';
import { cn } from '@/lib/utils';

import {
  getColumnHeaderJustificationClassName,
  getColumnTextAlignmentClassName,
  getPinnedCellClassName,
  getPinnedCellStyles,
  type PinnedOffsets,
} from './dataTableCellStyles';
import type { DataTableFeatures } from './dataTableFeatures';
import DataTableColumnTitlePopoverContent from './DataTableColumnTitlePopoverContent';
import { type DataTableColumnDef } from './types';

type DataTableTableHeadProps<TData extends object> = {
  headerGroups: HeaderGroup<DataTableFeatures, TData>[];
  canScrollLeft: boolean;
  canScrollRight: boolean;
  sorting: SortingState;
  pinnedOffsets: PinnedOffsets;
};

type DataTableTableHeadCellProps<TData extends object> = {
  header: Header<DataTableFeatures, TData, unknown>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  sorting: SortingState;
  pinnedOffsets: PinnedOffsets;
};

const isFilterApplied = (filterValue: FilterOperatorValue | null | undefined) =>
  filterValue != null && (!Array.isArray(filterValue) || filterValue.length > 0);

const HEADER_TITLE_ROW_CLASS = 'inline-flex max-w-full min-w-0 flex-nowrap items-center gap-2';
const HEADER_TITLE_CLASS = 'min-w-0 whitespace-normal wrap-break-word';

const DataTableTableHeadCell = <TData extends object>({
  header,
  canScrollLeft,
  canScrollRight,
  sorting,
  pinnedOffsets,
}: DataTableTableHeadCellProps<TData>) => {
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const columnDefinition = header.column.columnDef as DataTableColumnDef<TData>;
  const filterComponent = columnDefinition.filterComponent;
  const actionContent = columnDefinition.actionContent;
  const isFilterable = filterComponent != null;
  const isSortable = header.column.getCanSort();
  const columnSort = sorting.find((sortEntry) => sortEntry.id === header.column.id);
  const sortDirection = columnSort ? (columnSort.desc ? 'desc' : 'asc') : false;
  const isSorted = columnSort != null;
  const filterValue = header.column.getFilterValue() as FilterOperatorValue | null | undefined;
  const isFiltered = isFilterApplied(filterValue);
  const filterStatusLabel = isFiltered || isSorted ? t`Filter applied` : t`No filter applied`;
  const headerAriaSort = !isSortable
    ? undefined
    : sortDirection === 'asc'
      ? 'ascending'
      : sortDirection === 'desc'
        ? 'descending'
        : 'none';
  const toggleSortingHandler = header.column.getToggleSortingHandler();
  const headerContent = flexRender(header.column.columnDef.header, header.getContext());
  const filterLabel =
    typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : undefined;

  return (
    <TableHead
      colSpan={header.colSpan}
      aria-sort={headerAriaSort}
      className={cn(
        'bg-white p-0 align-bottom',
        getColumnTextAlignmentClassName(header.column),
        getPinnedCellClassName(header.column, 'header', canScrollLeft, canScrollRight),
        isSorted && 'bg-gray-100',
      )}
      style={getPinnedCellStyles(header.column, pinnedOffsets)}
    >
      {header.isPlaceholder ? null : isFilterable ? (
        <div className="flex size-full min-h-7 items-end">
          <Popover open={isPopoverOpen} onOpenChange={setPopoverOpen} modal={false}>
            <PopoverTrigger
              nativeButton={false}
              render={
                <div
                  className={cn(
                    'flex h-full min-h-7 min-w-0 flex-1 items-end px-2 py-1.5 transition-colors',
                    getColumnTextAlignmentClassName(header.column),
                    getColumnHeaderJustificationClassName(header.column),
                    'cursor-pointer hover:bg-gray-200 focus-visible:bg-gray-200 focus-visible:outline-hidden',
                    isPopoverOpen && 'bg-gray-200',
                    isSorted && !isPopoverOpen && 'bg-gray-100',
                  )}
                >
                  <span className={HEADER_TITLE_ROW_CLASS}>
                    <span className={HEADER_TITLE_CLASS}>{headerContent}</span>
                    <span
                      className={cn(
                        'inline-flex size-4 shrink-0 items-center justify-center text-gray-600 [&_svg]:size-4',
                        (isFiltered || isSorted) && 'text-gray-900',
                      )}
                      data-testid={`${header.column.id}-filter`}
                    >
                      {isFiltered || isSorted ? (
                        <FilterAppliedIcon aria-hidden />
                      ) : (
                        <FilterIcon aria-hidden />
                      )}
                    </span>
                    <span className="sr-only">{filterStatusLabel}</span>
                  </span>
                </div>
              }
            />
            <PopoverContent side="bottom" align="end" className="w-auto min-w-72 gap-0 p-0">
              <DataTableColumnTitlePopoverContent
                dataKey={header.column.id}
                isSortable={isSortable}
                filter={filterValue}
                sortDirection={sortDirection}
                onClose={() => setPopoverOpen(false)}
                onApply={(nextFilter, nextSort) => {
                  if (isSortable) {
                    if (nextSort == null) {
                      if (columnSort != null) {
                        header
                          .getContext()
                          .table.setSorting((currentSorting) =>
                            (currentSorting ?? []).filter(
                              (sortEntry) => sortEntry.id !== header.column.id,
                            ),
                          );
                      }
                    } else {
                      const nextSortDesc = nextSort === 'desc';
                      if (!(columnSort?.desc === nextSortDesc && sorting.length === 1)) {
                        header.getContext().table.setSorting([
                          {
                            id: header.column.id,
                            desc: nextSortDesc,
                          },
                        ]);
                      }
                    }
                  }

                  header.column.setFilterValue(nextFilter ?? undefined);
                }}
              >
                {filterComponent({
                  compact: false,
                  name: header.column.id,
                  label: filterLabel,
                })}
              </DataTableColumnTitlePopoverContent>
            </PopoverContent>
          </Popover>
          {actionContent && (
            <span className="mr-3 ml-2 flex shrink-0 items-end">{actionContent}</span>
          )}
        </div>
      ) : isSortable ? (
        <div className="flex size-full min-h-7 items-end">
          <button
            type="button"
            className={cn(
              'flex h-full min-h-7 min-w-0 flex-1 cursor-pointer items-end px-2 py-1.5 transition-colors',
              getColumnTextAlignmentClassName(header.column),
              getColumnHeaderJustificationClassName(header.column),
              'hover:bg-gray-200 focus-visible:bg-gray-200 focus-visible:outline-hidden',
              isSorted && 'bg-gray-100',
            )}
            onClick={toggleSortingHandler}
          >
            <span className={HEADER_TITLE_ROW_CLASS}>
              <span className={HEADER_TITLE_CLASS}>{headerContent}</span>
              <span
                className={cn(
                  'inline-flex size-4 shrink-0 items-center justify-center text-gray-600 [&_svg]:size-4',
                  isSorted && 'text-gray-900',
                )}
              >
                {sortDirection === 'asc' ? (
                  <SortUpIcon aria-hidden />
                ) : (
                  <SortDownIcon aria-hidden />
                )}
              </span>
            </span>
          </button>
          {actionContent && (
            <span className="mr-3 ml-2 flex shrink-0 items-center">{actionContent}</span>
          )}
        </div>
      ) : (
        <div
          className={cn(
            'flex min-h-7 min-w-0 items-start px-2 py-1.5',
            getColumnHeaderJustificationClassName(header.column),
          )}
        >
          <span className={HEADER_TITLE_CLASS}>{headerContent}</span>
          {actionContent && <span className="ml-auto">{actionContent}</span>}
        </div>
      )}
    </TableHead>
  );
};

const DataTableTableHead = <TData extends object>({
  headerGroups,
  canScrollLeft,
  canScrollRight,
  sorting,
  pinnedOffsets,
}: DataTableTableHeadProps<TData>) => {
  return (
    <TableHeader className="bg-white">
      {headerGroups.map((headerGroup) => (
        <TableRow key={headerGroup.id} className="group hover:bg-transparent">
          {headerGroup.headers.map((header) => (
            <DataTableTableHeadCell
              key={header.id}
              header={header}
              canScrollLeft={canScrollLeft}
              canScrollRight={canScrollRight}
              sorting={sorting}
              pinnedOffsets={pinnedOffsets}
            />
          ))}
        </TableRow>
      ))}
    </TableHeader>
  );
};

export default DataTableTableHead;
